import React from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components';

import TrustLogo from '@rover/icons/svg/trust-logo.svg';
import { Box, Button, Display, Flex, Heading, Image, Link, Paragraph } from '@rover/kibble/core';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import RoverProtect from '@rover/shared/images/homepage/rover-protect-homepage.png';
import RoverProtectMobile from '@rover/shared/images/homepage/rover-protect-homepage-mobile.jpg';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';

import {
  HomepageBookSitterClick,
  InfoCardLearnMoreGuaranteeClick,
  LearnMoreClick,
} from '../utils/analyticsEvents';
import {
  HOME_HEADING_TEXT,
  HOME_SUB_HEADING_TEXT,
  HOME_SUBMIT_BUTTON_TEXT,
  LEARN_MORE_BUTTON_TEXT,
  LEARN_MORE_TEXT,
  MESSAGE_PHOTO_TEXT,
  ROVER_GUARANTEE_TEXT,
  SCREENED_PET_SITTERS_TEXT,
  SUPPORT_TEXT,
} from '../utils/RoverProtectSignals.constants';
import { StyledAnchor } from '../utils/styles';

const StyledImageMobile = styled(Image)`
  border-top-left-radius: ${({ theme }) => theme.radii.primary};
  border-top-right-radius: ${({ theme }) => theme.radii.primary};
`;

const StyledSpan = styled.span`
  display: block;
  margin: ${({ theme }) => `${theme.space['4x']} 0`};
  text-align: center;
`;

const HomePageSignals = (): JSX.Element => {
  const { current } = useCountryConfigurationShim();

  return (
    <Box marginBottom={['8x', '16x']}>
      <Flex
        my={['8x', '0x']}
        borderRadius="primary"
        border="2x solid"
        sx={{ boxShadow: 'border_primary' }}
        justifyContent="center"
        flexDirection="column"
        width="100%"
        height="auto"
      >
        <Box display={['block', 'none']}>
          <StyledImageMobile
            width="100%"
            height="auto"
            description={t`rover protect homepage image of human and pet`}
            src={RoverProtectMobile}
          />
        </Box>
        <Box p={['6x', '12x']}>
          <Flex gap="4x" flexDirection={['column', 'row']}>
            <Box flex="0 0 72%">
              <Box display="flex" flexWrap="wrap" alignItems="center">
                <Flex mr="4x">
                  <TrustLogo width={36} height={48} />
                </Flex>
                <Display size="400" mb="0x">
                  {HOME_HEADING_TEXT()}
                </Display>
              </Box>
              <Heading size="300" mt="4x" mb="6x" fontWeight="semibold">
                {HOME_SUB_HEADING_TEXT()}
              </Heading>
              <Paragraph mb="6x" webProps={{ maxWidth: '85%' }}>
                {SCREENED_PET_SITTERS_TEXT}
              </Paragraph>
              <Paragraph mb="6x" webProps={{ maxWidth: '100%' }}>
                {MESSAGE_PHOTO_TEXT}
              </Paragraph>
              <Paragraph mb="6x" webProps={{ maxWidth: '100%' }}>
                {ROVER_GUARANTEE_TEXT(current?.formattedVetCareReimbursementAmount || '$25,000')}{' '}
                <StyledAnchor
                  href={current?.roverGuaranteeUrl || '/rover-guarantee'}
                  target="_blank"
                  onClick={() => emitAnalyticsEvent(new InfoCardLearnMoreGuaranteeClick())}
                >
                  {LEARN_MORE_TEXT()}
                </StyledAnchor>
              </Paragraph>
              <Paragraph webProps={{ maxWidth: '100%' }} mb="8x">
                {SUPPORT_TEXT()}
              </Paragraph>
            </Box>
            <Box flex="0 0 28%" display={['none', 'block']}>
              <Image
                src={RoverProtect}
                width="100%"
                height="auto"
                resizeMethod="contain"
                description={t`rover protect homepage image of human and pet`}
              />
            </Box>
          </Flex>
          <Box>
            <Flex gap="2x" mt="4x" mb="8x" flexDirection={['column', 'row']}>
              <Button
                variant="primary"
                onClick={() => emitAnalyticsEvent(new HomepageBookSitterClick())}
                href="/search"
                data-testid="rover-protect-signal-book-a-sitter"
                flex="1"
              >
                {HOME_SUBMIT_BUTTON_TEXT()}
              </Button>
              <Button
                href="/rover-protect"
                webProps={{ hrefTarget: '_blank' }}
                variant="flat"
                flex="1"
                onClick={() => emitAnalyticsEvent(new LearnMoreClick())}
              >
                {LEARN_MORE_BUTTON_TEXT()}
              </Button>
            </Flex>
          </Box>
        </Box>
      </Flex>
      <StyledSpan>
        <Trans>
          Services booked on Rover are backed by{' '}
          <Link size="100" href="/rover-protect/">
            RoverProtect
          </Link>{' '}
          and{' '}
          <Link size="100" href="/terms/reservation-protection/">
            reservation protection
          </Link>
          .
        </Trans>
      </StyledSpan>
    </Box>
  );
};

export default HomePageSignals;
